import { Component, HostBinding, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { PageEvent } from "@angular/material/paginator";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { AuthService } from "../../auth/auth.service";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import type { CardOrder } from "../card-orders.interface";
import { CardOrdersService } from "../card-orders.service";
import {
  getFulfillmentStageLabelKey,
  getOrderItemsSummary,
  getOrderRecipientsSummary,
} from "../card-order.util";

@Component({
  selector: "app-card-orders-list",
  templateUrl: "./card-orders-list.component.html",
  styleUrls: ["./card-orders-list.component.scss"],
  standalone: false,
})
export class CardOrdersListComponent implements OnInit, OnDestroy {
  readonly getFulfillmentStageLabelKey = getFulfillmentStageLabelKey;
  readonly getOrderRecipientsSummary = getOrderRecipientsSummary;
  readonly isSelfServiceOrder: boolean;
  displayedColumns: string[];
  readonly pageSizeOptions = [10, 20, 100];
  readonly dataSource = new MatTableDataSource<CardOrder>([]);
  readonly filters: FormGroup;
  loading = true;
  total = 0;
  pageIndex = 0;
  pageSize = 20;
  private readonly destroy$ = new Subject<void>();

  @HostBinding("class.is-empty")
  get isEmpty(): boolean {
    return !this.loading && this.total === 0 && !this.isFilterActive;
  }

  private get searchPhrase(): string {
    const value = this.filters.get("search")?.value as string | null;
    return (value ?? "").trim();
  }

  get isFilterActive(): boolean {
    return Boolean(this.searchPhrase);
  }

  get showNoSearchResults(): boolean {
    return !this.loading && this.isFilterActive && this.total === 0;
  }

  constructor(
    private readonly ordersService: CardOrdersService,
    private readonly toastr: ToastrTranslatedService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly auth: AuthService,
    fb: FormBuilder,
  ) {
    this.isSelfServiceOrder = !this.auth.hasCompanyManagerAccess;
    this.displayedColumns = this.isSelfServiceOrder
      ? ["createdAt", "items", "status", "actions"]
      : ["createdAt", "recipients", "items", "status", "actions"];
    this.filters = fb.group({ search: [""] });
  }

  ngOnInit(): void {
    this.filters
      .get("search")
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadOrders();
      });

    this.loadOrders();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadOrders(): void {
    this.loading = true;
    this.ordersService
      .listOrders({
        page: this.pageIndex,
        limit: this.pageSize,
        phrase: this.searchPhrase || undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.data = response.orders;
          this.total = response.total;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.toastr.error("cardOrders.loadFailed");
        },
      });
  }

  changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadOrders();
  }

  createOrder(): void {
    void this.router.navigate(["/card-orders/create"]);
  }

  orderDetailLink(order: CardOrder): string[] | null {
    const orderId = (order.printCardOrderId || "").trim();
    return orderId ? ["/card-orders", orderId] : null;
  }

  openOrder(order: CardOrder): void {
    const link = this.orderDetailLink(order);
    if (!link) {
      this.toastr.error("cardOrders.loadFailed");
      return;
    }
    void this.router.navigate(link);
  }

  orderItemsSummary(order: CardOrder): string {
    return getOrderItemsSummary(
      order,
      (key, params) => this.translate.instant(key, params) as string,
    );
  }
}
